import { useNav, isKnownRoute } from "./router";
import { ROUTE_READ_PERM } from "./routeTable";
import { SCREENS, MIGRATED_ROUTES } from "./screens";
import { can, useUser, landingRouteForRole } from "./access";
import { navProfileForUser } from "./navProfiles";
import { useRailCatalog } from "./chrome-rail";
import { useViewport } from "./useResponsive";
import AppLauncher from "./AppLauncher";
import { Btn, Icon, PageState, TagLabel } from "@v0y4ge/jrni-ui";
import { EventOverlay } from "../screens/events/detail/EventOverlay";
import { ErrorBoundary } from "./ErrorBoundary";
import { Screen as ScreenChrome } from "./chrome";
import { useT } from "../lib/i18n";

/**
 * AppRouter — renders the active route's screen, enforcing the read-level
 * capability guard (deep-link protection). Screens own their own chrome via the
 * ported <Screen> layout; this just resolves route → component.
 */
export function AppRouter() {
  const t = useT();
  const nav = useNav();
  const vp = useViewport();
  // Phone & tablet home is the app launcher; the full Studio is desktop-only.
  if (nav.route === "dashboard" && vp !== "desktop") return <AppLauncher />;
  const migrated = MIGRATED_ROUTES.has(nav.route);
  const perm = ROUTE_READ_PERM[nav.route];
  // Every screen and the event drawer render inside their own boundary, so a
  // render crash is contained to that surface instead of unmounting the shell
  // (2026-07-27). `resetKey` clears a caught error as soon as the route changes,
  // which makes navigating away the natural recovery.
  const drawer = nav.drawerEvent
    ? <ErrorBoundary scope={`drawer:${nav.drawerEvent}`} label={t("shell.appRouter.label", "Event drawer")} variant="pane" resetKey={String(nav.drawerEvent)}><EventOverlay ev={nav.drawerEvent} /></ErrorBoundary>
    : null;
  if (perm && !can(perm)) return <><NoAccess />{drawer}</>;
  /* An address that names no route at all (a mistyped or stale link): say so,
     inside the normal chrome, at the address the user opened — never a silent
     detour to the dashboard (see router.tsx parseLoc). */
  if (!isKnownRoute(nav.route)) return <><ScreenChrome><UnknownAddress route={nav.route} /></ScreenChrome>{drawer}</>;
  const Screen = SCREENS[nav.route];
  if (!Screen) return <><UnmigratedBadge /><NotFound route={nav.route} />{drawer}</>;
  return (
    <>
      {!migrated ? <UnmigratedBadge /> : null}
      <ErrorBoundary scope={nav.route} label={nav.route} resetKey={nav.route}
        wrap={(fallback) => <ScreenChrome>{fallback}</ScreenChrome>}><Screen /></ErrorBoundary>
      {drawer}
    </>
  );
}

/**
 * Unmigrated badge — fixed top-right, very high z-index, non-interactive.
 * Shows on every route NOT in MIGRATED_ROUTES (not yet wired to the live data
 * layer). A screen graduates — and the badge disappears — the moment it's added
 * to REAL_SCREENS. Built on the design-system TagLabel (token-based, no hex).
 */
function UnmigratedBadge() {
  const t = useT();
  return (
    <div aria-label={t("shell.appRouter.thisScreenIsNotYetMigratedToTheLiveDataLayer", "This screen is not yet migrated to the live data layer")}
      style={{ position: "fixed", top: 12, right: 12, zIndex: 2000000, pointerEvents: "none", borderRadius: 999, boxShadow: "0 4px 14px rgba(20, 16, 46, 0.18)" }}>
      <TagLabel color="orange" type="dot">{t("shell.appRouter.unmigrated", "Unmigrated")}</TagLabel>
    </div>
  );
}

function Centered({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, fontFamily: "var(--jrni-font-family-sans)" }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "var(--jrni-color-neutral-2)", lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}
function NoAccess() {
  const t = useT(); return <Centered title={t("shell.appRouter.youDonTHaveAccessToThis", "You don't have access to this")} body={t("shell.appRouter.yourRoleCanTOpenThisSurfaceAskAnAdminIfYou", "Your role can't open this surface. Ask an admin if you need access.")} />; }
/** The nearest route above an unknown address that this role may open —
 *  /workforce/myschedule/2026-12-01 → workforce.myschedule. Null when none. */
function readableParent(route: string): string | null {
  const parts = route.split(".");
  for (let n = parts.length - 1; n > 0; n--) {
    const r = parts.slice(0, n).join(".");
    if (!isKnownRoute(r)) continue;
    const perm = ROUTE_READ_PERM[r];
    if (!perm || can(perm)) return r;
  }
  return null;
}
function UnknownAddress({ route }: { route: string }) {
  const t = useT();
  const nav = useNav();
  const u = useUser();
  const rail = useRailCatalog();
  const home = navProfileForUser(u)?.landing || landingRouteForRole(u.role);
  const parent = readableParent(route);
  const parentLabel = parent ? rail.find((i) => i.route === parent)?.label : undefined;
  const homeLabel = rail.find((i) => i.route === home)?.label;
  const path = typeof window !== "undefined" ? window.location.pathname : "/" + route.split(".").join("/");
  return (
    <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
      <PageState empty testid="unknown-address" style={{ maxWidth: 560, width: "100%" }}
        icon={<Icon.search size={20} />}
        emptyTitle={t("shell.appRouter.unknownAddressTitle", "This page doesn't exist")}
        emptyBody={t("shell.appRouter.unknownAddressBody.icu", "Nothing in Studio lives at {path}. The link may be mistyped or out of date.", { path })}
        action={parent && parent !== home
          ? <Btn onClick={() => nav.replace(parent)}>{parentLabel ? t("shell.appRouter.openNamed.icu", "Open {name}", { name: parentLabel }) : t("shell.appRouter.openParent", "Open the page above")}</Btn>
          : <Btn onClick={() => nav.replace(home)}>{homeLabel ? t("shell.appRouter.goToNamed.icu", "Go to {name}", { name: homeLabel }) : t("shell.appRouter.goHome", "Go to your home page")}</Btn>}
        secondaryAction={parent && parent !== home
          ? <Btn variant="secondary" onClick={() => nav.replace(home)}>{homeLabel ? t("shell.appRouter.goToNamed.icu", "Go to {name}", { name: homeLabel }) : t("shell.appRouter.goHome", "Go to your home page")}</Btn>
          : undefined} />
    </div>
  );
}
function NotFound({ route }: { route: string }) {
  const t = useT(); return <Centered title={t("shell.appRouter.pageNotFound", "Page not found")} body={t("shell.appRouter.noScreenIsRegisteredForRoute.icu", "No screen is registered for \"{route}\".", { route })} />; }
